/**
 * v0.9.6 C3–C6 — pure logic of the past-storm player (no DOM, no map, no summary store), so it is
 * unit-tested and can never write into the live summary/result (C5).
 * Track files: /data/storms-past/<SID>.json (IBTrACS v04r01, scripts/build_storms_past.py).
 * Rules: the position between two source points is a straight-line interpolation of THOSE points;
 * never extended before the first or after the last point. Wind = the value of the latest source
 * point at or before t (as the agency gave it) — a point without one → "ไม่มีข้อมูลความแรง".
 */
export interface PastPoint { t: string; lat: number; lon: number; windKt: number | null; windAgency: string | null; interpolated: boolean; inThailand: boolean }
export interface PastStorm {
  sid: string; nameEn: string; nameTh: string | null; nameThSource: { url: string; quote: string } | null;
  yearBE: number; entryUtc: string; entryBearingDeg: number;
  source: string; citation: string; citationUrl: string; dataUrl: string; terms?: string;
  points: PastPoint[];
}
export interface PastFrame {
  lon: number; lat: number;        // interpolated centre
  bearing: number | null;          // heading of the current segment (deg, 0 = north)
  wind: { kt: number; agency: string | null; at: string } | null;
  inThailand: boolean;             // the bracketing source point (or the segment) is inside Thailand
  ended: boolean;                  // t is past the last point (icon fades, no extrapolation)
}

export const ms = (iso: string): number => Date.parse(iso.endsWith("Z") ? iso : `${iso}Z`);

/** Time span of the track [first, last] in ms (UTC). */
export function trackSpan(s: PastStorm): [number, number] {
  return [ms(s.points[0]!.t), ms(s.points[s.points.length - 1]!.t)];
}

/** Where the storm is at time t (ms UTC) — interpolation between the bracketing source points only. */
export function frameAt(s: PastStorm, t: number): PastFrame {
  const P = s.points, n = P.length;
  const t0 = ms(P[0]!.t), t1 = ms(P[n - 1]!.t);
  if (t <= t0) return { lon: P[0]!.lon, lat: P[0]!.lat, bearing: n > 1 ? bearingDeg(P[0]!, P[1]!) : null, wind: windAt(P, 0), inThailand: P[0]!.inThailand, ended: false };
  if (t >= t1) return { lon: P[n - 1]!.lon, lat: P[n - 1]!.lat, bearing: n > 1 ? bearingDeg(P[n - 2]!, P[n - 1]!) : null, wind: windAt(P, n - 1), inThailand: P[n - 1]!.inThailand, ended: t > t1 };
  let i = 0;
  while (i < n - 2 && ms(P[i + 1]!.t) <= t) i++;
  const a = P[i]!, b = P[i + 1]!, ta = ms(a.t), tb = ms(b.t);
  const f = tb > ta ? (t - ta) / (tb - ta) : 0;
  return {
    lon: a.lon + (b.lon - a.lon) * f, lat: a.lat + (b.lat - a.lat) * f,
    bearing: bearingDeg(a, b), wind: windAt(P, i), inThailand: a.inThailand || b.inThailand, ended: false,
  };
}

/** Latest source point ≤ index i that carries a wind value — only if it is THAT point (no carrying old winds forward). */
function windAt(P: PastPoint[], i: number): PastFrame["wind"] {
  const p = P[i]!;
  return p.windKt == null ? null : { kt: p.windKt, agency: p.windAgency, at: p.t };
}

export function bearingDeg(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const r = Math.PI / 180, y = Math.sin((b.lon - a.lon) * r) * Math.cos(b.lat * r);
  const x = Math.cos(a.lat * r) * Math.sin(b.lat * r) - Math.sin(a.lat * r) * Math.cos(b.lat * r) * Math.cos((b.lon - a.lon) * r);
  return ((Math.atan2(y, x) / r) + 360) % 360;
}

const DIR8 = ["ทิศเหนือ", "ทิศตะวันออกเฉียงเหนือ", "ทิศตะวันออก", "ทิศตะวันออกเฉียงใต้", "ทิศใต้", "ทิศตะวันตกเฉียงใต้", "ทิศตะวันตก", "ทิศตะวันตกเฉียงเหนือ"];
/** A storm HEADING b comes FROM the opposite side: heading 270° (west) → "เข้ามาจากทางทิศตะวันออก". */
export function fromDirection(headingDeg: number): string {
  return DIR8[Math.round((((headingDeg + 180) % 360) / 45)) % 8]!;
}

const THMON = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const DOW = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
/** Thai time "อ. 27 ก.ย. 2565 บ่าย" (UTC+7, Buddhist year, part of day). */
export function thaiWhen(t: number, withYear = true): string {
  const d = new Date(t + 7 * 3600 * 1000);
  const h = d.getUTCHours();
  const part = h < 6 ? "กลางคืน" : h < 12 ? "เช้า" : h < 18 ? "บ่าย" : "ค่ำ";
  return `${DOW[d.getUTCDay()]} ${d.getUTCDate()} ${THMON[d.getUTCMonth()]}${withYear ? ` ${d.getUTCFullYear() + 543}` : ""} ${part}`;
}

/** "โนรู (NORU) ปี 2565" — Thai name only when an official source gave one. */
export function stormTitle(s: PastStorm): string {
  const en = s.nameEn.replace(/:/g, " / ");
  return `${s.nameTh ? `${s.nameTh} (${en})` : en} ปี ${s.yearBE}`;
}

/** Wind line: knots as the agency gave them (+ km/h), or the owner's wording when absent. */
export function windText(w: PastFrame["wind"]): string {
  if (!w) return "ไม่มีข้อมูลความแรง";
  const AG: Record<string, string> = { WMO: "WMO", USA: "JTWC", TOKYO: "JMA", NEWDELHI: "IMD" };
  return `ลม ${w.kt} นอต (~${Math.round(w.kt * 1.852)} กม./ชม.)${w.agency ? ` · ${AG[w.agency] ?? w.agency}` : ""}`;
}
